/**
 * Tenant detail page (refreshed).
 *
 * Hero card with status + actions menu. Tabs separate overview from
 * lifecycle settings. Behaviour preserved: rename, status toggle,
 * soft-delete, restore — all super-only and server-gated.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  Building2,
  Key,
  MoreVertical,
  Pause,
  Play,
  Plug,
  RotateCcw,
  Trash2,
  Users,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { ApiKeysTab } from "./ApiKeysTab";
import { ProvidersTab } from "./ProvidersTab";
import { WebhookEndpointsTab } from "./WebhookEndpointsTab";
import {
  asApiError,
  deleteTenant,
  getTenant,
  patchTenant,
  restoreTenant,
  type TenantRow,
  type TenantStatus,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";

function tenantInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

export function TenantDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuper = user?.role === "super";

  const query = useQuery({
    queryKey: ["tenant", id] as const,
    queryFn: () => getTenant(id),
    enabled: Boolean(id),
  });

  if (query.isLoading) {
    return <DetailSkeleton />;
  }
  if (query.error || !query.data) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
          <div>
            <CardTitle className="text-base">Couldn't load this tenant</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              It may not exist or you may not have access.{" "}
              <Link to="/tenants" className="text-foreground underline">
                Back to tenants
              </Link>
            </p>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <TenantDetailLoaded
      key={query.data.id}
      tenant={query.data}
      isSuper={isSuper}
      onTenantChanged={async () => {
        await qc.invalidateQueries({ queryKey: ["tenant", id] });
        await qc.invalidateQueries({ queryKey: ["tenants"] });
      }}
      onDeleted={async () => {
        await qc.invalidateQueries({ queryKey: ["tenants"] });
        navigate("/tenants", { replace: true });
      }}
    />
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-9 w-72" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/**
 * Human-readable labels for each known feature flag. Source of truth
 * for the server's whitelist lives in
 * ``apps/core/senti_core/tenancy/feature_flags.py::KNOWN_FLAGS`` —
 * when adding a new flag there, add a label here too. The server
 * serializer surfaces every known flag as a key in
 * ``tenant.feature_flags`` so a missing label here would still render
 * (as the raw flag name), just less friendly.
 */
const FEATURE_FLAG_LABELS: Record<string, { title: string; help: string }> = {
  status_query_enabled: {
    title: "Status query",
    help: "Gates POST /api/v1/payment_intents/{id}/sync_status (spec 0004 §8.2). Operator opts in per tenant after smoke-test.",
  },
  receipt_lookup_enabled: {
    title: "Receipt lookup",
    help: "Gates POST /api/v1/inbound_payments/lookup (spec 0007 §8.2). Required for the receipt-keyed verify-and-materialise flow.",
  },
};

function TenantDetailLoaded({
  tenant,
  isSuper,
  onTenantChanged,
  onDeleted,
}: {
  tenant: TenantRow;
  isSuper: boolean;
  onTenantChanged: () => Promise<void>;
  onDeleted: () => Promise<void>;
}) {
  const [name, setName] = useState(tenant.name);
  const [nameError, setNameError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Local mirror of the tenant's feature_flags — operator toggles
  // checkboxes here; the diff against ``tenant.feature_flags`` is
  // sent on Save. The parent (``TenantDetailPage``) passes
  // ``key={query.data.id}`` so this component remounts on tenant
  // switch — local state resets cleanly there.
  const [flagsDraft, setFlagsDraft] = useState<Record<string, boolean>>(
    () => ({ ...(tenant.feature_flags ?? {}) }),
  );
  // Track the previous server-side feature_flags so that when the
  // server state refreshes (a concurrent operator's save → react-query
  // invalidates → refetch), we can distinguish "this key matches the
  // prior server value, take the new value" from "operator has flipped
  // this locally, preserve their intent." See PR-159 review #1 —
  // without this, an operator with an unsaved toggle gets it silently
  // overwritten when a parallel save lands.
  const priorServerFlagsRef = useRef<Record<string, boolean>>(tenant.feature_flags ?? {});

  useEffect(() => {
    setName(tenant.name);
    setNameError(null);
  }, [tenant.id, tenant.name]);

  useEffect(() => {
    const newServer = tenant.feature_flags ?? {};
    const prior = priorServerFlagsRef.current;
    setFlagsDraft((draft) => {
      // For every key in the new server state: if the operator hasn't
      // diverged from the prior server value, take the new server
      // value (they'll see another operator's change reflected). If
      // they HAVE diverged, preserve their in-progress flip — the
      // Save button's disabled-when-no-diff logic will continue to
      // gate the PATCH, and the explicit Save action will resolve
      // the conflict against the (newly-refreshed) server state.
      const merged: Record<string, boolean> = {};
      for (const [name, serverValue] of Object.entries(newServer)) {
        const draftValue = draft[name];
        const priorValue = prior[name];
        if (draftValue === undefined || draftValue === priorValue) {
          merged[name] = serverValue;
        } else {
          merged[name] = draftValue;
        }
      }
      return merged;
    });
    priorServerFlagsRef.current = newServer;
  }, [tenant.feature_flags]);

  // Cached "is the Save button enabled?" computation. Cheap at 2
  // flags today; the useMemo is a future-proofing for when
  // KNOWN_FLAGS grows. PR-159 review nit.
  const flagsHaveDiff = useMemo(() => {
    const server = tenant.feature_flags ?? {};
    return Object.entries(flagsDraft).some(([name, value]) => server[name] !== value);
  }, [flagsDraft, tenant.feature_flags]);

  const renameMutation = useMutation({
    mutationFn: () => patchTenant(tenant.id, { name: name.trim() }),
    onSuccess: async () => {
      setNameError(null);
      toast.success("Tenant renamed");
      await onTenantChanged();
    },
    onError: (err) => {
      const body = asApiError(err);
      setNameError(
        body?.error === "duplicate_name"
          ? "A tenant with that name already exists."
          : (body?.detail ?? "Rename failed."),
      );
    },
  });

  const statusMutation = useMutation({
    mutationFn: (s: TenantStatus) => patchTenant(tenant.id, { status: s }),
    onSuccess: async (updated) => {
      toast.success(`Tenant marked as ${updated.status}`);
      await onTenantChanged();
    },
    onError: (err) => {
      toast.error("Status change failed", {
        description: asApiError(err)?.detail ?? undefined,
      });
    },
  });

  const featureFlagsMutation = useMutation({
    mutationFn: () => {
      // Send only the keys whose value diverges from the server's
      // current state (partial-merge semantics on the backend).
      const serverFlags = tenant.feature_flags ?? {};
      const diff: Record<string, boolean> = {};
      for (const [name, value] of Object.entries(flagsDraft)) {
        if (serverFlags[name] !== value) {
          diff[name] = value;
        }
      }
      return patchTenant(tenant.id, { feature_flags: diff });
    },
    onSuccess: async () => {
      toast.success("Feature flags saved");
      await onTenantChanged();
    },
    onError: (err) => {
      toast.error("Couldn't save feature flags", {
        description: asApiError(err)?.detail ?? undefined,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteTenant(tenant.id),
    onSuccess: async () => {
      toast.success("Tenant deleted");
      await onDeleted();
    },
    onError: (err) => {
      toast.error("Couldn't delete tenant", {
        description: asApiError(err)?.detail ?? undefined,
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => restoreTenant(tenant.id),
    onSuccess: async () => {
      toast.success("Tenant restored");
      await onTenantChanged();
    },
    onError: (err) => {
      toast.error("Couldn't restore tenant", {
        description: asApiError(err)?.detail ?? undefined,
      });
    },
  });

  const isDeleted = tenant.deleted_at !== null;
  const newStatus: TenantStatus = tenant.status === "active" ? "suspended" : "active";

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 h-auto px-2 text-muted-foreground">
        <Link to="/tenants">
          <ArrowLeft /> Back to tenants
        </Link>
      </Button>

      <Card className="mb-6 brand-edge p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
                {tenantInitials(tenant.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight">{tenant.name}</h1>
                <Badge
                  variant={tenant.environment === "live" ? "success" : "warning"}
                  className="capitalize"
                >
                  {tenant.environment}
                </Badge>
                <Badge
                  variant={tenant.status === "active" ? "success" : "muted"}
                  className="capitalize"
                >
                  {tenant.status}
                </Badge>
                {isDeleted ? (
                  <Badge variant="destructive">Deleted</Badge>
                ) : null}
              </div>
              <code className="block truncate font-mono text-xs text-muted-foreground">
                {tenant.id}
              </code>
              <p className="text-xs text-muted-foreground">
                Created {new Date(tenant.created_at).toLocaleString()}
                {tenant.deleted_at
                  ? ` · Deleted ${new Date(tenant.deleted_at).toLocaleString()}`
                  : ""}
              </p>
            </div>
          </div>

          {isSuper ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" aria-label="Tenant actions">
                  <MoreVertical />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {!isDeleted ? (
                  <>
                    <DropdownMenuItem
                      onClick={() => statusMutation.mutate(newStatus)}
                      disabled={statusMutation.isPending}
                    >
                      {tenant.status === "active" ? <Pause /> : <Play />}
                      Mark as {newStatus}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setConfirmDelete(true)}
                      className="text-destructive focus:text-destructive [&_svg]:text-destructive"
                    >
                      <Trash2 />
                      Delete tenant
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem
                    onClick={() => restoreMutation.mutate()}
                    disabled={restoreMutation.isPending}
                  >
                    <RotateCcw />
                    Restore tenant
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="related">Related</TabsTrigger>
          <TabsTrigger value="api-keys">API keys</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          {isSuper ? <TabsTrigger value="settings">Settings</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-3">
            <CountCard
              label="Operators"
              value={tenant.counts?.operators ?? null}
              icon={Users}
              to="/operators"
              accent="primary"
            />
            <CountCard
              label="API keys"
              value={tenant.counts?.api_keys ?? null}
              icon={Key}
              accent="info"
            />
            <CountCard
              label="Provider accounts"
              value={tenant.counts?.provider_accounts ?? null}
              icon={Plug}
              accent="warning"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                <Def label="Name">{tenant.name}</Def>
                <Def label="ID" mono>
                  {tenant.id}
                </Def>
                <Def label="Environment">
                  <Badge
                    variant={tenant.environment === "live" ? "success" : "warning"}
                    className="capitalize"
                  >
                    {tenant.environment}
                  </Badge>
                </Def>
                <Def label="Status">
                  <Badge
                    variant={tenant.status === "active" ? "success" : "muted"}
                    className="capitalize"
                  >
                    {tenant.status}
                  </Badge>
                </Def>
                <Def label="Created">{new Date(tenant.created_at).toLocaleString()}</Def>
                {isDeleted ? (
                  <Def label="Deleted">
                    {tenant.deleted_at
                      ? new Date(tenant.deleted_at).toLocaleString()
                      : "—"}
                  </Def>
                ) : null}
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="related">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Related records</CardTitle>
              <p className="text-xs text-muted-foreground">
                Drill into payments and inbound traffic scoped to this tenant.
              </p>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              <RelatedLink
                to={`/payment-intents?tenant_id=${tenant.id}`}
                title="Payment intents"
                description="Outbound collects, payouts, and refunds for this tenant."
                icon={Building2}
              />
              <RelatedLink
                to={`/inbound-payments?tenant_id=${tenant.id}`}
                title="Inbound payments"
                description="Customer-initiated paybill / till receipts."
                icon={Building2}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api-keys" className="space-y-4">
          <ApiKeysTab tenantId={tenant.id} />
        </TabsContent>

        <TabsContent value="providers" className="space-y-4">
          <ProvidersTab tenantId={tenant.id} />
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4">
          <WebhookEndpointsTab tenantId={tenant.id} />
        </TabsContent>

        {isSuper ? (
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Rename</CardTitle>
                <p className="text-xs text-muted-foreground">
                  The audit log captures every rename's before/after.
                </p>
              </CardHeader>
              <CardContent>
                <form
                  className="flex flex-col gap-3 sm:flex-row sm:items-end"
                  onSubmit={(e) => {
                    e.preventDefault();
                    renameMutation.mutate();
                  }}
                >
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="rename-name">Name</Label>
                    <Input
                      id="rename-name"
                      value={name}
                      maxLength={200}
                      onChange={(e) => setName(e.target.value)}
                      disabled={renameMutation.isPending || isDeleted}
                    />
                    {nameError ? (
                      <p className="text-xs text-destructive">{nameError}</p>
                    ) : null}
                  </div>
                  <Button
                    type="submit"
                    disabled={
                      renameMutation.isPending ||
                      isDeleted ||
                      name.trim() === "" ||
                      name.trim() === tenant.name
                    }
                  >
                    {renameMutation.isPending ? "Saving…" : "Save"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Status</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Suspended tenants stay in the database but operators should
                  treat them as paused.
                </p>
              </CardHeader>
              <CardContent className="flex items-center justify-between gap-2">
                <p className="text-sm">
                  Current:{" "}
                  <Badge
                    variant={tenant.status === "active" ? "success" : "muted"}
                    className="capitalize"
                  >
                    {tenant.status}
                  </Badge>
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => statusMutation.mutate(newStatus)}
                  disabled={statusMutation.isPending || isDeleted}
                >
                  {tenant.status === "active" ? <Pause /> : <Play />}
                  Mark as {newStatus}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Feature flags</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Per-tenant opt-in for staged-rollout features. The audit log
                  captures every flag flip. Unknown flag names are rejected by
                  the server (typo defence).
                </p>
              </CardHeader>
              <CardContent>
                <form
                  className="space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    featureFlagsMutation.mutate();
                  }}
                >
                  {Object.entries(flagsDraft)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([flagName, value]) => {
                      const label = FEATURE_FLAG_LABELS[flagName];
                      const inputId = `flag-${flagName}`;
                      return (
                        <label
                          key={flagName}
                          htmlFor={inputId}
                          className="flex items-start gap-3 rounded-md border border-border/60 p-3"
                        >
                          <input
                            id={inputId}
                            type="checkbox"
                            checked={value}
                            disabled={featureFlagsMutation.isPending || isDeleted}
                            onChange={(e) =>
                              setFlagsDraft((prev) => ({
                                ...prev,
                                [flagName]: e.target.checked,
                              }))
                            }
                            className="mt-0.5 h-4 w-4 cursor-pointer"
                          />
                          <div className="flex-1 space-y-1">
                            <p className="text-sm font-medium">
                              {label?.title ?? flagName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {label?.help ?? "Unlabeled flag — add a label in FEATURE_FLAG_LABELS."}
                            </p>
                            <p className="font-mono text-[10px] text-muted-foreground/70">
                              {flagName}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  {Object.keys(flagsDraft).length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No feature flags registered.
                    </p>
                  ) : null}
                  {tenant.feature_flags_unknown &&
                  Object.keys(tenant.feature_flags_unknown).length > 0 ? (
                    <p className="text-xs text-amber-600">
                      Legacy keys present (not editable here):{" "}
                      <span className="font-mono">
                        {Object.keys(tenant.feature_flags_unknown).join(", ")}
                      </span>
                    </p>
                  ) : null}
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={
                        featureFlagsMutation.isPending || isDeleted || !flagsHaveDiff
                      }
                    >
                      {featureFlagsMutation.isPending ? "Saving…" : "Save flags"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle className="text-base text-destructive">Danger zone</CardTitle>
                <p className="text-xs text-muted-foreground">
                  {isDeleted
                    ? "This tenant is soft-deleted. Restore it to make it visible to operators again."
                    : "Soft-delete preserves the row for 7 years (Kenyan record-retention regime) and is reversible from this page."}
                </p>
              </CardHeader>
              <CardContent>
                {isDeleted ? (
                  <Button
                    variant="outline"
                    onClick={() => restoreMutation.mutate()}
                    disabled={restoreMutation.isPending}
                  >
                    <RotateCcw />
                    {restoreMutation.isPending ? "Restoring…" : "Restore tenant"}
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmDelete(true)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 />
                    Delete tenant
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}
      </Tabs>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this tenant?</DialogTitle>
            <DialogDescription>
              <span className="font-medium">{tenant.name}</span> will be hidden
              from the standard list but preserved for 7 years per Kenyan
              record-retention rules. You can restore it from this page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteMutation.mutate();
                setConfirmDelete(false);
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete tenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CountCard({
  label,
  value,
  icon: Icon,
  to,
  accent,
}: {
  label: string;
  value: number | null;
  icon: typeof Users;
  to?: string;
  accent: "primary" | "info" | "warning";
}) {
  const accentClass =
    accent === "primary"
      ? "bg-primary/10 text-primary"
      : accent === "info"
        ? "bg-info/10 text-info"
        : "bg-warning/15 text-warning";

  const body = (
    <Card className="flex items-center gap-3 p-4 transition-colors hover:bg-muted/30">
      <div className={`flex h-9 w-9 items-center justify-center rounded-md ${accentClass}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-mono text-lg font-semibold tabular-nums">
          {value === null ? "—" : value}
        </div>
      </div>
      {to ? <ArrowUpRight className="h-4 w-4 text-muted-foreground" /> : null}
    </Card>
  );

  if (to) {
    return <Link to={to}>{body}</Link>;
  }
  return body;
}

function RelatedLink({
  to,
  title,
  description,
  icon: Icon,
}: {
  to: string;
  title: string;
  description: string;
  icon: typeof Users;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-lg border bg-card p-4 transition-colors hover:bg-muted/30"
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 font-medium">
          {title}
          <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
    </Link>
  );
}

function Def({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd
        className={
          mono
            ? "mt-1 break-all font-mono text-xs"
            : "mt-1 text-sm font-medium"
        }
      >
        {children}
      </dd>
    </div>
  );
}

export default TenantDetailPage;
